import React, { useState } from 'react';
import { Box, Typography, IconButton, Menu, MenuItem } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EditIcon from '@mui/icons-material/Edit';
import ArticleIcon from '@mui/icons-material/Article';
import ImageIcon from '@mui/icons-material/Image';

const isDev = process.env.NODE_ENV === 'development';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Custom styles for markdown content
export const markdownStyles = {
  p: {
    marginBottom: '1rem',
    lineHeight: 1.6,
    paddingRight: '48px',
  },
  h1: {
    fontSize: '1.8rem',
    marginBottom: '1rem',
    marginTop: '1.5rem',
    paddingRight: '48px',
  },
  h2: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    marginTop: '1.5rem',
    paddingRight: '48px',
  },
  h3: {
    fontSize: '1.2rem',
    marginBottom: '0.8rem',
    marginTop: '1.2rem',
    paddingRight: '48px',
  },
  ul: {
    marginBottom: '1rem',
    paddingLeft: '2rem',
  },
  ol: {
    marginBottom: '1rem',
    paddingLeft: '2rem',
  },
  li: {
    marginBottom: '0.5rem',
  },
  blockquote: {
    borderLeft: '4px solid #ccc',
    paddingLeft: '1rem',
    marginLeft: 0,
    marginRight: 0,
    fontStyle: 'italic',
  },
  img: {
    maxWidth: '100%',
    height: 'auto'
  }
};

const MarkdownContent = ({ content, onRewriteClick, onHeaderImageClick }) => {
  const [menuPosition, setMenuPosition] = useState(null);
  const [selectedHeader, setSelectedHeader] = useState({ text: '', level: 0 });

  const getTextContent = (children) => {
    if (!Array.isArray(children)) return children;
    
    return children.map(child => {
      if (typeof child === 'string') return child;
      if (child.props && child.props.children) {
        // Handle nested elements (like bold, italic, etc.)
        return getTextContent(child.props.children);
      }
      return '';
    }).join('');
  };

  const handleHeaderClick = (event, children, element) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const level = parseInt(element.slice(1), 10);
    setMenuPosition({
      left: rect.left,
      top: rect.top + window.scrollY,
    });
    setSelectedHeader({ text: getTextContent(children), level });
  };

  const handleMenuClose = () => {
    setMenuPosition(null);
  };

  const handleMenuItemClick = (type) => {
    if (type === 'section') {
      const lines = content.split('\n');
      let headerIndex = -1;
      let nextHeaderIndex = -1;
      const headerPrefix = '#'.repeat(selectedHeader.level);
      
      // Find the current header
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].trim() === `${headerPrefix} ${selectedHeader.text}`) {
          headerIndex = i;
          break;
        }
      }

      if (headerIndex === -1) return;

      // Find the next header of same or higher level
      for (let i = headerIndex + 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.startsWith('#')) {
          const currentHeaderLevel = line.split(' ')[0].length;
          if (currentHeaderLevel <= selectedHeader.level) {
            nextHeaderIndex = i;
            break;
          }
        }
      }

      // Get all content between headers
      const sectionContent = lines.slice(
        headerIndex,
        nextHeaderIndex === -1 ? undefined : nextHeaderIndex
      ).join('\n');

      onRewriteClick(sectionContent);
    } else if (type === 'image') {
      // For image attachment, pass the header text and section content
      const lines = content.split('\n');
      let headerIndex = -1;
      let nextHeaderIndex = -1;
      const headerPrefix = '#'.repeat(selectedHeader.level);
      
      // Find the current header
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].trim() === `${headerPrefix} ${selectedHeader.text}`) {
          headerIndex = i;
          break;
        }
      }

      if (headerIndex === -1) return;

      // Find the next header of same or higher level
      for (let i = headerIndex + 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.startsWith('#')) {
          const currentHeaderLevel = line.split(' ')[0].length;
          if (currentHeaderLevel <= selectedHeader.level) {
            nextHeaderIndex = i;
            break;
          }
        }
      }

      // Get all content between headers
      const sectionContent = lines.slice(
        headerIndex,
        nextHeaderIndex === -1 ? undefined : nextHeaderIndex
      ).join('\n');

      onHeaderImageClick(selectedHeader.text, sectionContent, selectedHeader.level);
    } else {
      // For text-only, include the markdown header syntax
      const headerPrefix = '#'.repeat(selectedHeader.level);
      onRewriteClick(`${headerPrefix} ${selectedHeader.text}`);
    }
    handleMenuClose();
  };

  const renderWithAiButton = (Element, props, children, isHeader = false) => (
    <Box
      sx={{
        position: 'relative',
        '&:hover .ai-button': { opacity: 1 },
        mb: Element === 'p' ? 1 : 2,
      }}
    >
      <Element {...props}>{children}</Element>
      <IconButton
        className="ai-button"
        size="small"
        onClick={isHeader ? 
          (e) => handleHeaderClick(e, children, Element) : 
          () => onRewriteClick(getTextContent(children))
        }
        sx={{
          position: 'absolute',
          right: '4px',
          top: '50%',
          transform: 'translateY(-50%)',
          opacity: 0,
          transition: 'opacity 0.2s',
          backgroundColor: 'background.paper',
          boxShadow: 1,
          width: '36px',
          height: '36px',
          color: 'primary.main',
          '& .MuiSvgIcon-root': {
            fontSize: '1.2rem',
          },
          '&:hover': {
            backgroundColor: 'primary.main',
            color: 'common.white',
          },
        }}
      >
        <AutoFixHighIcon />
      </IconButton>
    </Box>
  );

  const components = {
    p: ({ children, ...props }) => renderWithAiButton('p', props, children),
    h1: ({ children, ...props }) => renderWithAiButton('h1', props, children, true),
    h2: ({ children, ...props }) => renderWithAiButton('h2', props, children, true),
    h3: ({ children, ...props }) => renderWithAiButton('h3', props, children, true),
    ul: ({ children }) => <Box component="ul" sx={markdownStyles.ul}>{children}</Box>,
    ol: ({ children }) => <Box component="ol" sx={markdownStyles.ol}>{children}</Box>,
    li: ({ children }) => <Box component="li" sx={markdownStyles.li}>{children}</Box>,
    blockquote: ({ children }) => renderWithAiButton('blockquote', {}, children),
    img: ({ src, alt }) => {
      const imageUrl = src.startsWith('http') ? src : `${API_BASE_URL}${src}`;
      return <img src={imageUrl} alt={alt} style={markdownStyles.img} />;
    },
    table: ({ children, ...props }) => renderWithAiButton('table', props, children),
  };

  return (
    <Box sx={{ 
      '& > *:first-child': { mt: 0 },
      '& > *:last-child': { mb: 0 },
    }}>
      <ReactMarkdown components={components}>
        {content}
      </ReactMarkdown>

      <Menu
        open={Boolean(menuPosition)}
        onClose={handleMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={menuPosition}
        sx={{
          '& .MuiPaper-root': {
            minWidth: '180px',
          }
        }}
      >
        <MenuItem onClick={() => handleMenuItemClick('section')} sx={{ gap: 1 }}>
          <ArticleIcon fontSize="small" />
          Edit whole section
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('text')} sx={{ gap: 1 }}>
          <EditIcon fontSize="small" />
          Edit text only
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('image')} sx={{ gap: 1 }}>
          <ImageIcon fontSize="small" />
          Attach Image to Header
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default MarkdownContent; 